/**
 * PostReactions — emoji bar shown beneath a post.
 *
 * Behavior:
 *   - Read of counts works without auth (public visitors see the
 *     numbers).
 *   - Clicking a reaction requires auth; unauthenticated users see a
 *     subtle "Sign in to react" hint instead of an action.
 *   - One reaction per user per post (Facebook-style). Clicking the
 *     SAME emoji you've already reacted with toggles it off; clicking
 *     a DIFFERENT one replaces.
 *   - Optimistic update on click — if the server rejects we snap back.
 */

import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  ReactionType,
  REACTION_LABELS,
  PostReactionsSummary,
  listPostReactions,
  setPostReaction,
} from "../../api/postsAPI";
import { useTheme } from "../../context/ThemeContext";
import { getCardBackground, getLightBorder, getMutedText } from "../../utils/themeUtils";
import { safeGetItem } from "@mediatechgroup/app-version";

interface Props {
  postId: string;
  enabled: boolean;
}

const ALL_TYPES: ReactionType[] = ["like", "love", "laugh", "wow", "celebrate", "sad"];

const emptyCounts = (): Record<ReactionType, number> => ({
  like: 0,
  love: 0,
  laugh: 0,
  wow: 0,
  celebrate: 0,
  sad: 0,
});

const PostReactions: React.FC<Props> = ({ postId, enabled }) => {
  const { isDarkMode } = useTheme();
  const token = safeGetItem("authToken");
  const [summary, setSummary] = useState<PostReactionsSummary>({
    counts: emptyCounts(),
    total: 0,
    my_reaction: null,
  });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!enabled || !postId) return;
    let cancelled = false;
    (async () => {
      const res = await listPostReactions(token, postId);
      if (cancelled) return;
      if (res.success && res.data) setSummary(res.data);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [postId, enabled, token]);

  if (!enabled) return null;
  if (loading) {
    return <div className={`text-xs ${getMutedText(isDarkMode)} mt-4`}>Loading reactions…</div>;
  }

  const handleClick = async (type: ReactionType) => {
    if (!token) {
      toast.info("Sign in to react");
      return;
    }
    if (busy) return;

    // Optimistic: snapshot then mutate.
    const prev = summary;
    const wasMine = summary.my_reaction;
    const next = {
      counts: { ...summary.counts },
      total: summary.total,
      my_reaction: summary.my_reaction,
    };
    if (wasMine === type) {
      // toggle off
      next.counts[type] = Math.max(0, next.counts[type] - 1);
      next.total = Math.max(0, next.total - 1);
      next.my_reaction = null;
    } else if (wasMine) {
      // replace
      next.counts[wasMine] = Math.max(0, next.counts[wasMine] - 1);
      next.counts[type] = next.counts[type] + 1;
      next.my_reaction = type;
    } else {
      next.counts[type] = next.counts[type] + 1;
      next.total = next.total + 1;
      next.my_reaction = type;
    }
    setSummary(next);
    setBusy(true);

    const res = await setPostReaction(token, postId, type);
    setBusy(false);
    if (!res.success) {
      // Snap back on failure
      setSummary(prev);
      toast.error(res.error || "Failed to set reaction");
    }
  };

  return (
    <div
      className={`mt-6 p-3 rounded-lg border ${getLightBorder(isDarkMode)} ${getCardBackground(
        isDarkMode
      )} flex items-center gap-2 flex-wrap`}
    >
      {ALL_TYPES.map((t) => {
        const meta = REACTION_LABELS[t];
        const count = summary.counts[t] || 0;
        const isMine = summary.my_reaction === t;
        return (
          <button
            key={t}
            onClick={() => handleClick(t)}
            disabled={busy}
            title={meta.label}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-sm transition-all ${
              isMine
                ? isDarkMode
                  ? "bg-blue-900/50 ring-1 ring-blue-500"
                  : "bg-blue-50 ring-1 ring-blue-400"
                : isDarkMode
                ? "bg-gray-800 hover:bg-gray-700"
                : "bg-gray-100 hover:bg-gray-200"
            } disabled:opacity-50`}
          >
            <span className="text-base leading-none">{meta.emoji}</span>
            {count > 0 && (
              <span className={`text-xs font-medium ${getMutedText(isDarkMode)}`}>{count}</span>
            )}
          </button>
        );
      })}
      {!token && (
        <span className={`text-xs italic ml-2 ${getMutedText(isDarkMode)}`}>
          Sign in to react
        </span>
      )}
    </div>
  );
};

export default PostReactions;
